import React from "react";
import PropTypes from "prop-types";
import { Link, Outlet, useLocation } from "react-router-dom";
import "../styles/Dashboard.css";

const FEES_MODULES = [
  {
    id: "list",
    title: "Fee List & Payment",
    description: "View all students and manage their fee payments.",
    icon: "💳",
    link: "/admin/manage-fees/list"
  },
  {
    id: "bulk",
    title: "Bulk Fee Upload",
    description: "Upload fees for multiple students using Excel.",
    icon: "📥",
    link: "/admin/manage-fees/bulk"
  },
  {
    id: "reports",
    title: "Fee Reports",
    description: "Generate and download fee collection and dues reports.",
    icon: "📊",
    link: "/admin/manage-fees/reports"
  },
  {
    id: "scholarship",
    title: "Scholarship/Concession",
    description: "Apply and manage scholarships or concessions.",
    icon: "🎓",
    link: "/admin/manage-fees/scholarship"
  }
];

/**
 * ManageFeesHome
 *
 * - Shows a dashboard of fee-related modules when the route is /admin/manage-fees
 * - If routed deeper (e.g. /admin/manage-fees/list) it renders the child routes via <Outlet />
 * - Role-aware: accepts a `user` prop and shows/hides modules based on role.
 *   - admin: sees all modules
 *   - principal: sees only list & reports (no bulk upload / scholarship management)
 *   - accountant / other roles: can be adjusted in the rolePermissions object
 *
 * Usage:
 *  <ManageFeesHome user={currentUser} />
 *
 * If you don't pass a user prop, all modules will be shown (backwards compatible).
 */
export default function ManageFeesHome({ user }) {
  const location = useLocation();
  const atRoot = location.pathname === "/admin/manage-fees";

  // Define which modules are visible per role.
  // Tweak permissions here as your app's policies require.
  const rolePermissions = {
    admin: ["list", "bulk", "reports", "scholarship"],
    principal: ["list", "reports"], // principal can view list and reports (no bulk upload or manage scholarship)
    accountant: ["list", "bulk", "reports"],
    default: FEES_MODULES.map(m => m.id) // if user not provided, show all (fallback)
  };

  const userRole = user?.role;
  const allowed = rolePermissions[userRole] ?? rolePermissions.default;

  const visibleModules = FEES_MODULES.filter(m => allowed.includes(m.id));

  if (!atRoot) {
    return <Outlet />;
  }

  return (
    <div className="dashboard-wrapper" role="region" aria-label="Manage Fees Dashboard">
      <h2>Manage Fees</h2>
      <p>Select an action below to manage fees and payment records.</p>

      <div className="dashboard-cards" role="list">
        {visibleModules.map((mod) => {
          const isActive = location.pathname === mod.link || location.pathname.startsWith(mod.link + "/");
          return (
            <Link
              to={mod.link}
              className={`dashboard-card ${isActive ? "active" : ""}`}
              key={mod.id}
              aria-label={mod.title}
              role="listitem"
            >
              <div className="dashboard-card-icon" aria-hidden>{mod.icon}</div>
              <div className="dashboard-card-title">{mod.title}</div>
              <div className="dashboard-card-desc">{mod.description}</div>
            </Link>
          );
        })}

        {visibleModules.length === 0 && (
          <div className="dashboard-empty" role="status">
            You don't have permissions to access any fee modules.
          </div>
        )}
      </div>
    </div>
  );
}

ManageFeesHome.propTypes = {
  user: PropTypes.object
};